import { PermissionCode, ProductCode } from '@gb-ticket/shared-types';
import { Navigate, type RouteObject } from 'react-router';

import { RequireAuthLayout, RequireProductLayout } from '@/platform/auth/require-permission';
import { PlatformShellLayout } from '@/platform/layouts/platform-shell';
import '@/products/leave/product-nav';
import { LeaveAdminGate } from '@/products/leave/features/leave-admin-gate';
import { LeaveLayout } from '@/products/leave/layouts/leave-layout';
import { LeaveAdminLayout } from '@/products/leave/layouts/leave-admin-layout';
import { LEAVE_ADMIN_TYPES_PATH, LEAVE_ROOT } from '@/products/leave/leave-paths';
import { LeaveAdminAbsenceTypesPage } from '@/products/leave/pages/leave-admin-absence-types-page';
import { LeaveAdminBalancesPage } from '@/products/leave/pages/leave-admin-balances-page';
import { LeaveAdminCapacityPage } from '@/products/leave/pages/leave-admin-capacity-page';
import { LeaveAdminExportPage } from '@/products/leave/pages/leave-admin-export-page';
import { LeaveAdminHolidaysPage } from '@/products/leave/pages/leave-admin-holidays-page';
import { LeaveAdminHomePage } from '@/products/leave/pages/leave-admin-home-page';
import { LeaveAdminPolicyPage } from '@/products/leave/pages/leave-admin-policy-page';
import { LeaveColleaguesLayout } from '@/products/leave/layouts/leave-colleagues-layout';
import { LeaveCalendarPage } from '@/products/leave/pages/leave-calendar-page';
import {
  LeaveAdminColleagueCalendarPage,
  LeaveTeamCalendarPage,
} from '@/products/leave/pages/leave-colleague-calendar-page';
import { LeaveTeamPage } from '@/products/leave/pages/leave-team-page';
import { LeaveDelegatePage } from '@/products/leave/pages/leave-delegate-page';
import { LeaveHomePage } from '@/products/leave/pages/leave-home-page';
import { LeaveInboxPage } from '@/products/leave/pages/leave-inbox-page';
import { LeaveRequestDetailPage } from '@/products/leave/pages/leave-request-detail-page';
import { LeaveRequestPage } from '@/products/leave/pages/leave-request-page';
import { LeaveRequestsPage } from '@/products/leave/pages/leave-requests-page';

/** Leave product (`/leave`) inside the Mitarbeiterportal chrome. */
export const leaveRoute: RouteObject = {
  Component: RequireAuthLayout,
  children: [
    {
      Component: PlatformShellLayout,
      children: [
        {
          path: LEAVE_ROOT,
          element: <RequireProductLayout product={ProductCode.LEAVE} />,
          children: [
            {
              Component: LeaveLayout,
              children: [
                { index: true, Component: LeaveHomePage },
                { path: 'beantragen', Component: LeaveRequestPage },
                { path: 'antraege', Component: LeaveRequestsPage },
                { path: 'antraege/:id', Component: LeaveRequestDetailPage },
                { path: 'eingang', Component: LeaveInboxPage },
                { path: 'vertretung', Component: LeaveDelegatePage },
                { path: 'kalender', Component: LeaveCalendarPage },
                {
                  path: 'kolleginnen',
                  Component: LeaveColleaguesLayout,
                  children: [
                    { index: true, Component: LeaveTeamPage },
                    { path: 'kalender', Component: LeaveTeamCalendarPage },
                    {
                      path: 'kalender/verwaltung',
                      element: (
                        <LeaveAdminGate permission={PermissionCode.LEAVE_REQUESTS_DECIDE_ALL} />
                      ),
                      children: [{ index: true, Component: LeaveAdminColleagueCalendarPage }],
                    },
                  ],
                },
                {
                  path: 'verwaltung',
                  element: <LeaveAdminGate permission={PermissionCode.LEAVE_POLICY_MANAGE} />,
                  children: [
                    {
                      Component: LeaveAdminLayout,
                      children: [
                        { index: true, Component: LeaveAdminHomePage },
                        { path: 'richtlinie', Component: LeaveAdminPolicyPage },
                        { path: 'feiertage', Component: LeaveAdminHolidaysPage },
                        { path: 'arten', Component: LeaveAdminAbsenceTypesPage },
                        {
                          path: 'sonderurlaub',
                          element: <Navigate to={LEAVE_ADMIN_TYPES_PATH} replace />,
                        },
                        { path: 'besetzung', Component: LeaveAdminCapacityPage },
                      ],
                    },
                  ],
                },
                {
                  path: 'verwaltung/salden',
                  element: <LeaveAdminGate permission={PermissionCode.LEAVE_BALANCES_ADJUST} />,
                  children: [
                    {
                      Component: LeaveAdminLayout,
                      children: [{ index: true, Component: LeaveAdminBalancesPage }],
                    },
                  ],
                },
                {
                  path: 'verwaltung/export',
                  element: <LeaveAdminGate permission={PermissionCode.LEAVE_REPORTS_EXPORT} />,
                  children: [
                    {
                      Component: LeaveAdminLayout,
                      children: [{ index: true, Component: LeaveAdminExportPage }],
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
  ],
};
